import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { StoreProvider } from "./context/StoreContext";
import Preloader from "./components/Preloader";
import Cursor from "./components/Cursor";
import Header from "./components/Header";
import CartDrawer from "./components/CartDrawer";
import FloatingCart from "./components/FloatingCart";
import BottomNav from "./components/BottomNav";
import SearchOverlay from "./components/SearchOverlay";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminPage from "./pages/AdminPage";

function useLenis() {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({
            duration: 1.15,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        });
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);
}

function Curtain() {
    const location = useLocation();
    return (
        <motion.div
            key={location.pathname + location.search}
            data-testid="page-curtain"
            className="pointer-events-none fixed inset-0 z-[140] bg-[#F2F0EA]"
            initial={{ clipPath: "inset(0 0 0 0)" }}
            animate={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
        />
    );
}

function ScrollReset() {
    const location = useLocation();
    const first = useRef(true);
    useEffect(() => {
        if (first.current) {
            first.current = false;
            return;
        }
        window.scrollTo(0, 0);
    }, [location.pathname, location.search]);
    return null;
}

function Site() {
    useLenis();
    const isAdmin = useLocation().pathname.startsWith("/admin");
    if (isAdmin) {
        return (
            <div className="min-h-screen bg-[#F2F0EA] text-[#0A0A0A]">
                <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#ECEAE4", border: "1px solid rgba(10,10,10,0.12)", color: "#0A0A0A", borderRadius: 0, fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase" } }} />
                <Routes>
                    <Route path="/admin" element={<AdminPage />} />
                </Routes>
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-[#F2F0EA] text-[#0A0A0A]">
            <Preloader />
            <Cursor />
            <Header />
            <CartDrawer />
            <FloatingCart />
            <BottomNav />
            <SearchOverlay />
            <Curtain />
            <ScrollReset />
            <main>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/shop" element={<ShopPage />} />
                    <Route path="/product/:id" element={<ProductPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                </Routes>
            </main>
            <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#ECEAE4", border: "1px solid rgba(10,10,10,0.12)", color: "#0A0A0A", borderRadius: 0, fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase" } }} />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <StoreProvider>
                <Site />
            </StoreProvider>
        </BrowserRouter>
    );
}

export default App;
